import React, { useState, useCallback, useMemo, useRef } from 'react';
import { graphql } from 'react-apollo';
import { useQuery, useApolloClient, useLazyQuery } from '@apollo/react-hooks';
import compose from 'lodash/flowRight';
import { useLocation } from 'react-router-dom';
import CURRENT_USER from '../../../../GraphQl/Queries/CURRENT_USER';
import CAMPAIGN_ORDER_TRANSACTIONS_BY_DATE from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_BY_DATE';
import CAMPAIGN_ORDER_TRANSACTIONS_BY_CREATIVES from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_CREATIVES';
import CAMPAIGN_ORDER_TRANSACTIONS_BY_PUBLISHERS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_PUBLISHERS';
import CAMPAIGN_ORDER_TRANSACTIONS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS';
import CAMPAIGN_ORDER_TRANSACTIONS_IMPRESSIONS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_IMPRESSIONS';
import CAMPAIGN_ORDER_TRANSACTIONS_DETAILS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_DETAILS';
import moment from 'moment-timezone';
import CAMPAIGN_ORDER_TRANSACTIONS_ALL from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_ALL';
import CAMPAIGN_ORDER_TRANSACTION_CREATIVES_PAGINATED from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTION_CREATIVES_PAGINATED';
import CAMPAIGN_ORDER_TRANSACTION_CREATIVES_BY_DATES_PAGINATED from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTION_CREATIVES_BY_DATES_PAGINATED';
import CAMPAIGN_ORDER_TRANSACTION_PUBLISHERS_BY_DATES_PAGINATED from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTION_PUBLISHERS_BY_DATES_PAGINATED';
import CAMPAIGN_ORDER_TRANSACTIONS_BY_CAMPAIGN_BY_DATE from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_BY_CAMPAIGN_BY_DATE';
import TRANSACTION_CREATIVES_BY_CAMPAIGN from '../../../../GraphQl/Queries/TRANSACTION_CREATIVES_BY_CAMPAIGN';
import TRANSACTION_PUBLISHERS_BY_CAMPAIGN from '../../../../GraphQl/Queries/TRANSACTION_PUBLISHERS_BY_CAMPAIGN';
import { useTimezone } from '../../../../hooks/useTimezone';

const TransactionsTabLoader = ({ children, ...props }) => {
  const { campaignOrder } = props;
  const client = useApolloClient();
  const location = useLocation();
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();

  // State for accumulated paginated data
  const [accumulatedCreativesByDate, setAccumulatedCreativesByDate] = useState([]);
  const [accumulatedPublishersByDate, setAccumulatedPublishersByDate] = useState([]);
  const [isFetchingCreatives, setIsFetchingCreatives] = useState(false);
  const [isFetchingPublishers, setIsFetchingPublishers] = useState(false);

  // State for cached campaign byDate data
  const [campaignByDateCache, setCampaignByDateCache] = useState({});
  const [campaignByDateLoading, setCampaignByDateLoading] = useState(false);

  // State for cached per-campaign creatives/publishers data
  const [creativeByCampaignCache, setCreativeByCampaignCache] = useState({});
  const [publisherByCampaignCache, setPublisherByCampaignCache] = useState({});
  const [creativeByCampaignLoading, setCreativeByCampaignLoading] = useState(false);
  const [publisherByCampaignLoading, setPublisherByCampaignLoading] = useState(false);

  // Refs to avoid cache in useCallback deps (prevents new function refs on cache update)
  const campaignByDateCacheRef = useRef(campaignByDateCache);
  campaignByDateCacheRef.current = campaignByDateCache;
  const creativeByCampaignCacheRef = useRef(creativeByCampaignCache);
  creativeByCampaignCacheRef.current = creativeByCampaignCache;
  const publisherByCampaignCacheRef = useRef(publisherByCampaignCache);
  publisherByCampaignCacheRef.current = publisherByCampaignCache;

  // Read pagination and date filter from URL params
  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;
  const pageSize = parseInt(queryParams.get('pageSize')) || 10;
  const startDateParam = queryParams.get('startDate');
  const endDateParam = queryParams.get('endDate');
  const attributionWindow = parseInt(queryParams.get('attributionWindow')) || 30;
  const attributionMethod = queryParams.get('attributionMethod') || 'ALL';
  const campaignIndexParam = queryParams.get('campaignIndex') || 'All';

  const { loading: byDateLoading, data: byDateData } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS_BY_DATE,
    {
      variables: {
        id: campaignOrder.id,
      },
    }
  );

  // Wrapper function to recursively fetch all pages of creatives by date
  const fetchAllCreativesByDate = useCallback(
    async (variables) => {
      setIsFetchingCreatives(true);
      setAccumulatedCreativesByDate([]);

      let allData = [];
      let currentSkip = 0;
      let hasNextPage = true;

      try {
        while (hasNextPage) {
          const result = await client.query({
            query: CAMPAIGN_ORDER_TRANSACTION_CREATIVES_BY_DATES_PAGINATED,
            variables: {
              ...variables,
              skip: currentSkip,
            },
            fetchPolicy: 'network-only',
          });

          const edges =
            result.data?.campaignOrderTransactionCreativesByDatesConnection?.edges || [];
          const pageData = edges.map((edge) => edge.node);
          allData = [...allData, ...pageData];

          hasNextPage =
            result.data?.campaignOrderTransactionCreativesByDatesConnection?.pageInfo
              ?.hasNextPage || false;
          currentSkip += variables.first || 1000;
        }

        setAccumulatedCreativesByDate(allData);
      } catch (error) {
        console.error('Error fetching creatives by date:', error);
      } finally {
        setIsFetchingCreatives(false);
      }
    },
    [client]
  );

  // Wrapper function to recursively fetch all pages of publishers by date
  const fetchAllPublishersByDate = useCallback(
    async (variables) => {
      setIsFetchingPublishers(true);
      setAccumulatedPublishersByDate([]);

      let allData = [];
      let currentSkip = 0;
      let hasNextPage = true;

      try {
        while (hasNextPage) {
          const result = await client.query({
            query: CAMPAIGN_ORDER_TRANSACTION_PUBLISHERS_BY_DATES_PAGINATED,
            variables: {
              ...variables,
              skip: currentSkip,
            },
            fetchPolicy: 'network-only',
          });

          const edges =
            result.data?.campaignOrderTransactionPublishersByDatesConnection?.edges || [];
          const pageData = edges.map((edge) => edge.node);
          allData = [...allData, ...pageData];

          hasNextPage =
            result.data?.campaignOrderTransactionPublishersByDatesConnection?.pageInfo
              ?.hasNextPage || false;
          currentSkip += variables.first || 1000;
        }

        setAccumulatedPublishersByDate(allData);
      } catch (error) {
        console.error('Error fetching publishers by date:', error);
      } finally {
        setIsFetchingPublishers(false);
      }
    },
    [client]
  );

  // Lazy fetch function for campaign byDate data
  const fetchCampaignByDate = useCallback(
    async (campaignId) => {
      // Return cached data if available
      if (campaignByDateCacheRef.current[campaignId]) {
        return campaignByDateCacheRef.current[campaignId];
      }

      setCampaignByDateLoading(true);
      try {
        const result = await client.query({
          query: CAMPAIGN_ORDER_TRANSACTIONS_BY_CAMPAIGN_BY_DATE,
          variables: { campaignId },
          fetchPolicy: 'network-only',
        });

        const byDateData = result.data?.transactionByCampaignByDates || [];

        // Cache the result
        setCampaignByDateCache((prev) => ({
          ...prev,
          [campaignId]: byDateData,
        }));

        return byDateData;
      } catch (error) {
        console.error('Error fetching campaign byDate:', error);
        return [];
      } finally {
        setCampaignByDateLoading(false);
      }
    },
    [client]
  );

  // Lazy fetch function for per-campaign creatives data
  const fetchCreativesByCampaign = useCallback(
    async (adProviderId) => {
      // Return cached data if available
      if (creativeByCampaignCacheRef.current[adProviderId]) {
        return creativeByCampaignCacheRef.current[adProviderId];
      }

      setCreativeByCampaignLoading(true);
      try {
        const result = await client.query({
          query: TRANSACTION_CREATIVES_BY_CAMPAIGN,
          variables: { adProviderId },
          fetchPolicy: 'network-only',
        });

        const creatives = result.data?.transactionCreativeByCampaigns || [];

        // Cache the result
        setCreativeByCampaignCache((prev) => ({
          ...prev,
          [adProviderId]: creatives,
        }));

        return creatives;
      } catch (error) {
        console.error('Error fetching creatives by campaign:', error);
        return [];
      } finally {
        setCreativeByCampaignLoading(false);
      }
    },
    [client]
  );

  // Lazy fetch function for per-campaign publishers data
  const fetchPublishersByCampaign = useCallback(
    async (adProviderId) => {
      // Return cached data if available
      if (publisherByCampaignCacheRef.current[adProviderId]) {
        return publisherByCampaignCacheRef.current[adProviderId];
      }

      setPublisherByCampaignLoading(true);
      try {
        const result = await client.query({
          query: TRANSACTION_PUBLISHERS_BY_CAMPAIGN,
          variables: { adProviderId },
          fetchPolicy: 'network-only',
        });

        const publishers = result.data?.transactionPublisherByCampaigns || [];

        // Cache the result
        setPublisherByCampaignCache((prev) => ({
          ...prev,
          [adProviderId]: publishers,
        }));

        return publishers;
      } catch (error) {
        console.error('Error fetching publishers by campaign:', error);
        return [];
      } finally {
        setPublisherByCampaignLoading(false);
      }
    },
    [client]
  );

  // Regular aggregate queries (always loaded)
  const { loading: alLCreativesLoading, data: allCreativesData } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS_BY_CREATIVES,
    {
      variables: {
        id: campaignOrder.id,
      },
    }
  );

  const { loading: publishersLoading, data: publishersData } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS_BY_PUBLISHERS,
    {
      variables: {
        id: campaignOrder.id,
      },
    }
  );

  // Load details data (includes transactionsByCampaign) - needed for campaign filtering
  const { loading: detailsLoading, data: detailsData } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS_DETAILS,
    {
      variables: {
        id: campaignOrder.id,
      },
    }
  );

  // Extract transactionsByCampaign from detailsData
  const transactionsByCampaignRaw =
    detailsData?.campaignOrderTransactionReports?.[0]?.transactionsByCampaign;
  const transactionsByCampaign = useMemo(
    () => transactionsByCampaignRaw || [],
    [transactionsByCampaignRaw]
  );

  // Build where clause with optional date filter from URL params
  const transactionsWhere = {
    campaignOrderTransactionReport: {
      analytics: {
        campaignOrder: {
          id: campaignOrder.id,
        },
      },
    },
  };

  // Add date filter if present in URL
  if (startDateParam && endDateParam) {
    const startMoment = effectiveTimezone === 'UTC'
      ? moment.utc(startDateParam).startOf('day')
      : moment.tz(startDateParam, effectiveTimezone).startOf('day');

    const endMoment = effectiveTimezone === 'UTC'
      ? moment.utc(endDateParam).add(1, 'day').startOf('day')
      : moment.tz(endDateParam, effectiveTimezone).add(1, 'day').startOf('day');

    transactionsWhere.transactionDate_gte = startMoment.toISOString();
    transactionsWhere.transactionDate_lt = endMoment.toISOString();
  }

  // Add attribution window filter
  transactionsWhere.attrWindow_lte = attributionWindow;

  // Add attribution method filter
  if (attributionMethod !== 'ALL') {
    transactionsWhere.attrMethod = attributionMethod;
  }

  // Add campaign filter if specific campaign selected
  if (
    campaignIndexParam !== 'All' &&
    transactionsByCampaign?.[parseInt(campaignIndexParam)]
  ) {
    transactionsWhere.adProviderId =
      transactionsByCampaign[parseInt(campaignIndexParam)].adProviderId;
  }

  const { loading: transactionsLoading, data: transactionsData } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS,
    {
      variables: {
        where: transactionsWhere,
        first: pageSize,
        skip: (currentPage - 1) * pageSize,
        orderBy: 'transactionDate_ASC',
      },
    }
  );

  const [getAllTransactions, { loading: allTransactionsLoading, data: allTransactionsData }] =
    useLazyQuery(CAMPAIGN_ORDER_TRANSACTIONS_ALL, {
      variables: {
        where: transactionsWhere,
        orderBy: 'transactionDate_ASC',
      },
    });

  // Create a wrapper function that uses Apollo Client's query method
  const getImpressions = ({ variables }) => {
    return client.query({
      query: CAMPAIGN_ORDER_TRANSACTIONS_IMPRESSIONS,
      variables,
      fetchPolicy: 'network-only',
    });
  };

  return React.cloneElement(children, {
    ...props,
    getImpressions,
    byDateData: byDateData?.transactionReportByDates,
    // By-date data (paginated, loaded on-demand) - use accumulated data if available
    creativesDataByDate: accumulatedCreativesByDate.length > 0 ? accumulatedCreativesByDate : null,
    publishersDataByDate:
      accumulatedPublishersByDate.length > 0 ? accumulatedPublishersByDate : null,
    // Aggregate data (always loaded)
    creativesData: allCreativesData?.campaignOrderTransactionCreativeses,
    publishersData: publishersData?.campaignOrderTransactionPublisherses,
    transactionsData: transactionsData?.campaignOrderTransactionsWithEventsesConnection,
    details: detailsData?.campaignOrderTransactionReports?.[0],
    transactionsByCampaign,
    campaignOrder,
    pageSize,
    pagination: transactionsData?.full?.aggregate,
    getAllTransactions,
    allTransactionsData: allTransactionsData?.campaignOrderTransactionsWithEventses,
    // Individual loading states for each component (combined aggregate + by-date + by-campaign)
    byDateLoading,
    creativesLoading: alLCreativesLoading || isFetchingCreatives || creativeByCampaignLoading,
    publishersLoading: publishersLoading || isFetchingPublishers || publisherByCampaignLoading,
    transactionsLoading,
    detailsLoading,
    allTransactionsLoading,
    // Pass individual by-date loading states as well
    creativesByDateLoading: isFetchingCreatives,
    publishersByDateLoading: isFetchingPublishers,
    // Pass wrapper functions for on-demand loading with pagination
    fetchAllCreativesByDate,
    fetchAllPublishersByDate,
    // Campaign byDate lazy loading
    fetchCampaignByDate,
    campaignByDateLoading,
    // Per-campaign creatives/publishers lazy loading
    fetchCreativesByCampaign,
    fetchPublishersByCampaign,
    creativeByCampaignLoading,
    publisherByCampaignLoading,
  });
};

export default compose(
  graphql(CURRENT_USER, {
    props: ({ data: { currentUser }, ownProps }) => {
      return ownProps.currentUser || { currentUser };
    },
  })
)(TransactionsTabLoader);
